import { Simplify } from 'type-fest';

import { Detection } from '@/api/historicalData/types';
import { useLiveSplitQuery } from '@/api/useLiveSplitQuery';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import {
    CombinedQueriesResult,
    CombinedQueryOrQueriesResult,
    CombinedQueryResult,
    QueryConfig,
    SpotterIdOrIds,
} from '@/common/react-query';
import { DETECTIONS_QUERY_KEY } from '@/config';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { DateRange, useGlobalStore } from '@/store';
import { SortOrder } from '@/utils';
import { mockDetectionData } from '@/utils/mockDataHelpers';

export type GetDetectionsParams = {
    spotterId: string;
    startDate: string;
    endDate: string;
    sortOrder: SortOrder;
    useCache: boolean;
};

export const getDetections = async (params: GetDetectionsParams, options?: RequestOptions): Promise<Detection[]> => {
    const { mockDataSwitches } = useGlobalStore.getState();

    if (mockDataSwitches.detections) {
        return mockDetectionData({
            startDate: new Date(params.startDate),
            endDate: new Date(params.endDate),
            sensorPosition: 1,
        });
    }

    const { spotterId, startDate, endDate, sortOrder, useCache } = params;

    const response = await axios.get(`/user-rest/devices/${spotterId}/detections`, {
        params: { start_date: startDate, end_date: endDate, sort_order: sortOrder, useCache },
        ...requestConfig(options),
    });
    return response.data;
};

export type UseDetectionsOptions = {
    dateRange?: DateRange | null;
    /** Sent as `sort_order`. Defaults to `desc`, which is what the endpoint returns when the param is omitted. */
    sortOrder?: SortOrder;
    config?: Omit<QueryConfig<typeof getDetections>, 'select'>;
} & SpotterIdOrIds;

export function useDetections(
    options: Simplify<Omit<UseDetectionsOptions, 'spotterIds'>>
): CombinedQueryResult<Detection[]>;
export function useDetections(
    options: Simplify<Omit<UseDetectionsOptions, 'spotterId'>>
): CombinedQueriesResult<Detection[]>;
export function useDetections({
    spotterId,
    spotterIds,
    dateRange,
    sortOrder = 'desc',
    config,
}: UseDetectionsOptions): CombinedQueryOrQueriesResult<Detection[]> {
    const isSensorHdrCachingEnabled = useFeatureFlag('trySensorHdrCache');

    return useLiveSplitQuery({
        queryKeyPrefix: DETECTIONS_QUERY_KEY,
        spotterId,
        spotterIds,
        dateRange,
        keyOptions: { sortOrder },
        fetchRange: ({ viewToken, signal, ...params }) =>
            getDetections({ ...params, sortOrder, useCache: isSensorHdrCachingEnabled }, { viewToken, signal }),
        /**
         * Both date bounds filter `event_end_time` server side, so an event that starts before the
         * boundary and ends after it belongs wholly to the live half.
         */
        timestampKey: 'end_time',
        sortOrder,
        config,
    });
}
